
import axios from 'axios';
import type { CampaignParticipantProps, CampaignProps, CompanyProps, CompetitionProps, EventProps, MatchProps, NewOverallAnalytics, OrderAnalyticsProps, OrderProps, PollCampaignProps, PublicPlayerProps, TournamentProps } from '../../types';
import { APIOverrides } from '../../ApiOverrides';
import moment, { type Moment } from 'moment-mini';

//let SOCIAL_SVC_API = '';
//let EVENT_SVC_API = '';
let MK_SVC_API = '';
let AUTH_SVC_API = '';
let TP_SVC_API = ''

export const CampaignApi = {
    setEnvironment: () => {
        const endpoints = APIOverrides.getEndpoints();
        //SOCIAL_SVC_API = endpoints['SOCIAL_SVC_API'] as string;
        //EVENT_SVC_API = endpoints['EVENT_SVC_API'] as string;
        MK_SVC_API = endpoints['MK_SVC_API'] as string;
        AUTH_SVC_API = endpoints['AUTH_SVC_API'] as string;
        TP_SVC_API = endpoints['TP_SVC_API'] as string;

    },
    getPlayersByPlayerIds : async(player_ids:string[]):Promise<PublicPlayerProps[]> => {
        try {
            if(player_ids.length == 0){ return [] }
            const resp = await axios.post(`${AUTH_SVC_API}/v1/players/bulk/get`, { player_ids })
            return resp.data.players
        } catch (e) {
            return []
        }
    },
    getCampaignById: async(campaign_id:string):Promise<{campaign?:CampaignProps, campaign_participants:CampaignParticipantProps[]}> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/campaigns/campaign/${campaign_id}`);
            return resp.data
        } catch (e) {
            return { campaign: undefined, campaign_participants:[]  }
        }
    },
    getPollCampaignsByIds: async(poll_campaign_ids:string[]):Promise<PollCampaignProps[]> => {
        try {
            if(poll_campaign_ids.length == 0){ return [] }
            const resp = await axios.post(`${AUTH_SVC_API}/v1/polls/campaigns/bulk/get`, { poll_campaign_ids });
            return resp.data.poll_campaigns
        } catch (e) {
            console.log(e)
            return []
        }
    },
    getCompetitionsByIds: async(competition_ids:string[]):Promise<CompetitionProps[]> => {
        try {
            if(competition_ids.length == 0){ return [] }
            const resp = await axios.post(`${TP_SVC_API}/v2/competitions/bulk/get`, { competition_ids });
            return resp.data.competitions
        } catch (e) {
            console.log(e)
            return []
        }
    },
    getCompaniesByIds: async(company_ids:string[]):Promise<CompanyProps[]> => {
        try {
            if(company_ids.length == 0){ return [] }
            const resp = await axios.post(`${AUTH_SVC_API}/v1/companies/bulk/get`, { company_ids });
            return resp.data.companies
        } catch (e) {
            return []
        }
    },
    joinCampaign: async(campaign_id:string):Promise<CampaignParticipantProps|undefined> => {
        try {
            const resp = await axios.post(`${AUTH_SVC_API}/v1/campaigns/campaign/join`, { campaign_id });
            return resp.data.campaign_participant
        } catch (e) {
            console.log(e)
            return undefined
        }
    },
    leaveCampaign: async(campaign_participant_id:string):Promise<CampaignParticipantProps|undefined> => {
        try {
            const resp = await axios.post(`${AUTH_SVC_API}/v1/campaigns/campaign/leave`, { campaign_participant_id });
            return resp.data.campaign_participant
        } catch (e) {
            return undefined
        }
    },
    getMyCampaignParticipants: async():Promise<{ campaigns:CampaignProps[], campaign_participants:CampaignParticipantProps[] }> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/campaigns/participant/me`);
            return resp.data
        } catch (e) {
            return { campaigns:[], campaign_participants:[] }
        }
    },
    getCampaignAction: async(campaign_id:string):Promise<OrderProps[]> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/orders/campaign/action/${campaign_id}`);
            return resp.data.orders
        } catch (e) {
            return []
        }
    },
    getCampaignAnalytics: async(campaign_id:string, to:number, from:number):Promise<OrderAnalyticsProps[]> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/analytics/campaign/details/${campaign_id}?to=${to}&from=${from}`);
            return resp.data.analytics
        } catch (e) {
            console.log(e);
            return []
        }
    }
}
export const CampaignHelpers = {
    aggregateAnalytics: (analytics:OrderAnalyticsProps[]):NewOverallAnalytics => {
        const wins = analytics.reduce((a,b) => a + b.wins, 0);
        const losses = analytics.reduce((a,b) => a + b.losses, 0);
        const draws = analytics.reduce((a,b) => a + b.draws, 0);
        const fulfilled_positions = analytics.reduce((a,b) => a + parseFloat(b.fulfilled_positions as any), 0);
        const winnings = analytics.reduce((a,b) => a + b.winnings, 0);
        const original_stake = analytics.reduce((a,b) => a + b.original_stake, 0);
        const cash_rcvd = analytics.reduce((a,b) => a + b.cash_rcvd, 0);
        
        const win_pct = (wins + (draws / 2)) / fulfilled_positions
        const earnings = (winnings + cash_rcvd) - original_stake
        const roi_pct = earnings / original_stake
        return {
            winnings,
            original_stake,
            wins,
            losses,
            draws,
            net_winnings: 0,
            player_id: '',
            fulfilled_positions,
            cash_rcvd,
            earnings,
            win_pct,
            roi_pct
        }
    },
    getCampaignLeaderboard: (analytics:OrderAnalyticsProps[]):NewOverallAnalytics[] => {
        let unique_player_ids = [ ...new Set(analytics.map(a => a.player_id)) ]
        let overall_analytics:NewOverallAnalytics[] = []
        unique_player_ids.map(id => {
            let anals = analytics.filter(a => a.player_id == id);
            let agg_analytics = CampaignHelpers.aggregateAnalytics(anals)
            overall_analytics.push({ ...agg_analytics, player_id: id })
        });
        return overall_analytics.sort((a,b) => b.earnings - a.earnings);
    },
    getTimeOptions: () => {
        const time_options = [
            { label: 'All Time', to: moment().unix(), from: moment().subtract(7, 'years').unix() },
            { label: 'Last 7 Days', to: moment().unix(), from: moment().subtract(7, 'days').unix() }
        ]
        return time_options
    },
    getContestDetails: (contest_type:'team'|'tournament'|'match', event?:EventProps, tournament?:TournamentProps, match?:MatchProps) => {
        const default_response = { contest_title: '', contest_details:'' }
        switch(contest_type){
            case 'team':
                if(!event){ return default_response }
                let details = ['created','scheduled'].includes(event.status) ? moment(event.scheduled_datetime).format('MMM dd hh:mm a') : event.status
                return { contest_title: event.event_title, contest_details: details }
            case 'tournament':
                if(!tournament){ return default_response }
                return { contest_title:tournament.tournament_name, contest_details: moment(tournament.scheduled_datetime).format('MMM dd hh:mm a') }
            case 'match':
                if(!match){ return default_response }
                return { contest_title:match.match_title, contest_details: moment(match.scheduled_datetime).format('MMM dd hh:mm a') }
            default: return default_response
        }
    },
    sortEngagements: (competitions:CompetitionProps[], poll_campaigns:PollCampaignProps[]) => {
        let sorted_engagements: { id:string, type:'poll_campaign'|'competition', scheduled_datetime:Moment }[] = []
        competitions.map(c => 
            sorted_engagements.push({ id: c.competition_id, type:'competition', scheduled_datetime: moment(c.scheduled_datetime) })
        )
        poll_campaigns.map(pc => 
            sorted_engagements.push({ id:pc.poll_campaign_id, type:'poll_campaign', scheduled_datetime:moment().add(2, 'hours')  })
        )
        return sorted_engagements.sort((a,b) => a.scheduled_datetime.unix() - b.scheduled_datetime.unix())
    }
}